/**
 * FILE: packages/supabase/src/students/phoneChanges.ts
 * PURPOSE: Immutable audit log for phone-number changes (merges/consolidations).
 */

import type { DbClient } from '../dbClient'
import { defaultDbClient } from '../dbClient'
import type { ActivityActor } from '../internal/activity'
import type { Json } from '../types'

export type PhoneChangeType = 'merge' | 'consolidate'

export interface PhoneChangeLogEntry {
  id: string
  old_phone: string
  new_phone: string
  change_type: PhoneChangeType
  actor_name: string | null
  actor_role: string | null
  reason: string | null
  details: Record<string, unknown>
  created_at: string
}

export interface PhoneChangeLogInsert {
  oldPhone: string
  newPhone: string
  changeType: PhoneChangeType
  actor: ActivityActor
  reason?: string
  details?: Record<string, unknown>
}

export async function insertPhoneChangeLog(
  payload: PhoneChangeLogInsert,
  options?: { swallowErrors?: boolean },
  client: DbClient = defaultDbClient
): Promise<void> {
  try {
    const { error } = await client.from('phone_change_log').insert({
      old_phone: payload.oldPhone,
      new_phone: payload.newPhone,
      change_type: payload.changeType,
      actor_user_id: payload.actor.userId,
      actor_name: payload.actor.userName,
      actor_role: payload.actor.userRole,
      reason: payload.reason ?? null,
      details: (payload.details ?? {}) as Json,
    })
    if (error) {
      console.error('insertPhoneChangeLog failed:', error)
      if (!options?.swallowErrors) throw error
    }
  } catch (e) {
    console.error('insertPhoneChangeLog exception:', e)
    if (!options?.swallowErrors) throw e
  }
}

export async function getPhoneChangeHistory(
  phone: string,
  client: DbClient = defaultDbClient
): Promise<PhoneChangeLogEntry[]> {
  const { data, error } = await client
    .from('phone_change_log')
    .select('*')
    .or(`old_phone.eq.${phone},new_phone.eq.${phone}`)
    .order('created_at', { ascending: false })

  if (error) throw error
  return (data ?? []) as PhoneChangeLogEntry[]
}
